import CloseIcon from '@mui/icons-material/Close';
import FormActionButtons from '../components/FormActionButtons';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Box, Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import images from '../components/images';
import PageMeta from '../components/PageMeta';
import { RecordDetails, RecordDetailsContext } from '../components/RecordDetails';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { activityTypeLabel } from './utils';

const useStyles = makeStyles((theme) => {
    console.log('theme', theme);
    return {
        root: {
            border: `1px solid ${grey[200]}`,
            borderRadius: '5px',
        },
        scrollableRoot: {
            overflowY: 'auto',
            height: `calc(${theme.cp.contentHeight} - 9.75rem) !important`,
            paddingTop: 0,
            [theme.breakpoints.down('sm')]: {
                height: `calc(${theme.cp.contentHeight} - 10rem) !important`,
            },
        },
        field: {
            padding: theme.spacing(1) + ' 0',
        },
        label: {
            color: '#747474',
            fontSize: '12px',
            lineHeight: '16px',
        },
        value: {
            color: '#202020',
            fontSize: '16px',
            lineHeight: '24px',
        },
        previewImage: {
            borderRadius: '10px',
        },
    };
});

const EMDASH = String.fromCharCode(8212);

const PreviewField = (props) => {
    const classes = useStyles();
    return (
        <div className={classes.field}>
            <div className={classes.label}>{props.label}</div>
            <div className={classes.value}>{props.value || EMDASH}</div>
        </div>
    );
};

const CPImage = (props) => {
    const { settings } = React.useContext(ContextStore);

    console.log('Image', props);
    const nximg = props.placeholder || <div className="non-existing-image-placeholder" />;

    if (!props.src) {
        return nximg;
    }
    const fit = props.fit || 'cover';
    const preset = `${fit}_${props.width}x${props.height}`;
    const imageURL = images.url(settings.images.base, props.src, preset);
    console.log('Image', imageURL);
    if (!imageURL) {
        return nximg;
    }

    return <Image src={imageURL} cover={true} aspectRatio={props.width / props.height} />;
};

const PreviewImage = (props) => {
    const classes = useStyles();
    const img = props.list[props.idx || 0];
    if (!img) {
        return <div />;
    }
    console.log('PreviewImage', props, img);
    return (
        <div style={{ marginRight: '1.5rem', marginBottom: '1rem', borderRadius: '10px', overflow: 'hidden' }}>
            <CPImage src={img.image} fit="cover" width={368} height={250} className={props.className || classes.previewImage} />
        </div>
    );
};

const ActivityPreviewPanel = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const isSmallScreen = !useIsWidthUp('sm');
    const history = useHistory();
    const classes = useStyles();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [syncActive, setSyncActive] = React.useState(false);

    const buttonSize = isSmallScreen ? 'small' : 'medium';

    const handleEdit = (ev) => {
        console.log('editing', props.data.id);
        history.push(`/activity/${props.data.id}`);
    };

    const handleDuplicate = (ev) => {
        console.log('duplicating', props.data.id);
        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(UPDATE, props.resource, {
                    urlSuffix: 'duplicate/',
                    id: props.data.id,
                });
                console.log('duplicate', res);
                enqueueSnackbar('Activity duplicated', {
                    variant: 'success',
                    action: (key) => (
                        <React.Fragment>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                    history.push(`/activity/${res.data.id}`);
                                }}
                            >
                                Open it
                            </Button>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                }}
                            >
                                Dismiss
                            </Button>
                        </React.Fragment>
                    ),
                });
            } catch (e) {
                enqueueSnackbar('Error duplicating activity', { variant: 'error' });
                console.error('Error duplicating activity', e);
            } finally {
                setSyncActive(false);
            }
        })();
    };

    return (
        <div className={classes.root}>
            <PageMeta title={`Activity - ${props.data.display_name}`} />
            <Box m={2}>
                <div className={classes.scrollableRoot}>
                    <Grid container direction="row" mb={2}>
                        <Grid item xs={8}>
                            <Typography variant="h6">Activity Details</Typography>
                        </Grid>
                        <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                            <Tooltip title="Close">
                                <IconButton
                                    aria-label="close-preview"
                                    onClick={props.onClose}
                                    size="small"
                                    sx={{
                                        color: 'rgba(0, 0, 0, 0.87)',
                                        '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.87)' },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <PreviewImage list={props.data.images} preset="cover_368x250" />
                    <PreviewField label="Display Name" value={props.data.display_name} />
                    <PreviewField label="Place" value={props.data.address} />
                    <PreviewField label="Activity Type" value={activityTypeLabel(props.data.type)} />
                    <PreviewField label="Internal ID" value={props.data.id} />
                </div>
                <Box mt={4}>
                    {/* CP-8427: bottom right, primary action on the right (Martina): [DUPLICATE] [DELETE] [EDIT]. */}
                    <FormActionButtons
                        size={buttonSize}
                        leadingActions={
                            <Button disabled={syncActive} size={buttonSize} color="primary" onClick={handleDuplicate}>
                                <FileCopyOutlinedIcon />
                                &nbsp;Duplicate
                            </Button>
                        }
                        onDelete={() => props.handleDelete([props.data.id])}
                        deletePerm="activity.delete"
                        deleteDisabled={syncActive}
                        onSubmit={handleEdit}
                        submitDisabled={syncActive}
                        submitLabel="Edit"
                        submitIcon={<EditIcon />}
                    />
                </Box>
            </Box>
        </div>
    );
};

const ActivityPreview = (props) => {
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm('activity', 'read')) {
        return <AccessDeniedView />
    }

    const handleOnLoad = (data) => {
        props.onLoad && props.onLoad(props.id, data);
    };
    return (
        <RecordDetails resource="activity" id={props.id} onLoad={handleOnLoad}>
            <RecordDetailsContext.Consumer>{(data) => <ActivityPreviewPanel resource={props.resource} id={props.id} data={data} onClose={props.onClose} handleDelete={props.handleDelete} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default ActivityPreview;
